import { IconTriangleExclamation } from 'central-icons/IconTriangleExclamation';
import { useCallback, useRef } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { Button, type ButtonProps, Dialog, useDisclosure } from '@revelio/ui';

import { openReplayStateLogger } from '../../hooks/open-replay';
import styles from './alert-dialog.module.css';

type AlertType = 'confirm' | 'actionless';

// Optional severity — renders a coloured icon next to the title.
type AlertStatus = 'warning' | 'error';

interface AlertProps {
  alertType: AlertType;
  /** Renders a warning/error icon next to the title. */
  status?: AlertStatus;
  onClose?: () => void;
  onAccept?: () => void;
  isOpen?: boolean;
  headerText: string;
  bodyContent: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  /** Overrides the Cancel button's Chakra color scheme. Defaults to 'navyBlue'. */
  cancelColorScheme?: string;
  /** Overrides the accept button's color scheme (e.g. 'orange' for a warning). */
  footerAcceptColorScheme?: string;
  /** Render only the accept button — no Cancel (e.g. an error acknowledgement). */
  hideCancel?: boolean;
  buttonLoading?: boolean;
  showCloseBtn?: boolean;
  skipDefaultDisclosure?: boolean;
}

export type IRevelioAlert = {
  alertType: AlertType;
  status?: AlertStatus;
  isOpen: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  headerText: string;
  bodyContent?: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  footerAcceptColorScheme?: string;
  hideCancel?: boolean;
  onAlertTriggered?: () => void;
};

const DEFAULT_ACCEPT_TEXT = 'Ok';

const toButtonAppearance = (
  colorScheme: string
): { color: ButtonProps['color']; className?: string } => {
  switch (colorScheme) {
    case 'red':
      return { color: 'destructive' };
    case 'gray':
      return { color: 'default' };
    case 'green':
    case 'brand':
      return { color: 'primary' };
    case 'blue':
      return { color: 'lightBlue' };
    case 'orange':
      return { color: 'default', className: styles.orangeButton };
    default:
      return { color: 'secondary' };
  }
};

interface AlertDialogStore {
  alert: IRevelioAlert | null;
}

const useAlertDialogStore = create<AlertDialogStore>()(
  devtools(
    openReplayStateLogger<AlertDialogStore>(
      () => ({
        alert: null,
      }),
      'alert_dialog'
    ),
    { name: 'AlertDialog' }
  )
);

export function showAlertDialog(alert: IRevelioAlert) {
  useAlertDialogStore.setState({ alert });
  alert.onAlertTriggered?.();
}

export const AlertDialog = ({
  isOpen,
  onClose,
  skipDefaultDisclosure = false,
  ...props
}: AlertProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const storeAlert = useAlertDialogStore((s) => s.alert);

  const alert = skipDefaultDisclosure ? null : storeAlert;

  const alertType = alert?.alertType ?? props.alertType;
  const status = alert?.status ?? props.status;
  const headerText = alert?.headerText ?? props.headerText;
  const bodyContent = alert?.bodyContent ?? props.bodyContent;
  const footerAcceptIsDestructive =
    alert?.footerAcceptIsDestructive ?? props.footerAcceptIsDestructive;
  const footerAcceptText =
    alert?.footerAcceptText ?? props.footerAcceptText ?? DEFAULT_ACCEPT_TEXT;
  const footerAcceptColorScheme =
    alert?.footerAcceptColorScheme ?? props.footerAcceptColorScheme;
  const hideCancel = alert?.hideCancel ?? props.hideCancel;
  const onAccept = alert?.onAccept ?? props.onAccept;
  const cancelAppearance = toButtonAppearance(
    props.cancelColorScheme ?? 'navyBlue'
  );
  const acceptAppearance = toButtonAppearance(
    footerAcceptColorScheme ?? (footerAcceptIsDestructive ? 'red' : 'navyBlue')
  );

  const { isOpen: isDialogOpen, onClose: disclosureOnClose } = useDisclosure({
    onClose,
    isOpen: alert?.isOpen ?? isOpen,
  });

  const combinedOnClose = useCallback(() => {
    alert?.onClose?.();
    useAlertDialogStore.setState({ alert: null });
    disclosureOnClose();
  }, [alert, disclosureOnClose]);

  return (
    <Dialog
      // An alert, not a plain dialog: it interrupts, so assistive tech
      // announces it on open. This is the whole reason it was an AlertDialog,
      // and it is how the confirm flows are found in tests.
      role="alertdialog"
      open={isDialogOpen}
      onClose={combinedOnClose}
      className={styles.content}
      // Was Chakra's `leastDestructiveRef` — focus opens on Cancel, never on
      // the destructive action.
      initialFocusRef={cancelRef}
      closeButton={props.showCloseBtn ?? false}
      title={
        status ? (
          <div className={styles.headerTitle}>
            <IconTriangleExclamation
              size={20}
              className={styles.statusIcon}
              data-error={status === 'error' || undefined}
            />
            <span>{headerText}</span>
          </div>
        ) : (
          headerText
        )
      }
      footer={
        alertType === 'actionless' ? undefined : (
          <>
            {!hideCancel && (
              <Button
                ref={cancelRef}
                color={cancelAppearance.color}
                className={cancelAppearance.className}
                variant="ghost"
                onClick={combinedOnClose}
              >
                Cancel
              </Button>
            )}
            <Button
              color={acceptAppearance.color}
              className={acceptAppearance.className}
              onClick={() => {
                combinedOnClose();
                onAccept?.();
              }}
              loading={props.buttonLoading}
            >
              {footerAcceptText}
            </Button>
          </>
        )
      }
    >
      <div
        className={styles.body}
        data-actionless={alertType === 'actionless' || undefined}
      >
        {bodyContent}
      </div>
    </Dialog>
  );
};
